import {CommonModule} from '@angular/common';
import {Component, EventEmitter, Input, Output} from '@angular/core';
import {FormBuilder, FormControl, ReactiveFormsModule} from '@angular/forms';
import {EmDatePipe, EmTranslatePipe, validators} from '@emma-labs/shared/angular';
import {SupportedLanguageCode, TextTranslations} from '@emma-labs/shared/typescript';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ButtonModule} from 'primeng/button';
import {DialogModule} from 'primeng/dialog';
import {DropdownModule} from 'primeng/dropdown';
import {InputTextareaModule} from 'primeng/inputtextarea';
import {ProfileDecisionPopupHeaderComponent} from '../profile-decision-popup-header.component';
import {hasNoInputPrompts} from '../utils';
import {TEMPLATE_OPTIONS} from './profile-refusal-message-templates';

export interface RefusalPopupInfo {
  clientFullName: string;
  policyNumber: string;
  languageCode: SupportedLanguageCode;
}

@UntilDestroy()
@Component({
  selector: 'em-profile-refusal-popup',
  templateUrl: './profile-refusal-popup.component.html',
  imports: [
    DialogModule,
    ReactiveFormsModule,
    InputTextareaModule,
    ButtonModule,
    CommonModule,
    DropdownModule,
    ProfileDecisionPopupHeaderComponent,
    EmTranslatePipe,
  ],
})
export class ProfileRefusalPopupComponent {
  @Input({required: true}) isDisplayed = false;

  @Input({required: true}) set info(info: RefusalPopupInfo) {
    this._info = info;

    this.headers = [
      {
        en: `Name of the insured: ${info.clientFullName}`,
        fr: `Nom de l'assuré : ${info.clientFullName}`,
      },
      {
        en: `Policy number: ${info.policyNumber}`,
        fr: `Numéro de police : ${info.policyNumber}`,
      },
    ];
  }

  @Output() isDisplayedChange = new EventEmitter<boolean>();
  @Output() submitRefusal = new EventEmitter<{
    refusalMessage: TextTranslations;
    callback: () => void;
  }>();

  isLoading = false;

  headers: TextTranslations[] = [];
  _info!: RefusalPopupInfo;
  readonly dropdownOptions = TEMPLATE_OPTIONS;
  readonly form: FormControl<string | null>;
  readonly dropdownForm: FormControl<string | null>;
  readonly createdAt = new Date();
  readonly refusalTimestamp = new EmDatePipe().transform(new Date().toString());
  readonly preInputParagraphs = [
    {
      en: 'Sir/Madam,',
      fr: 'Madame/Monsieur,',
    },
    {
      en: 'We have reviewed your insurance request and wish to inform you of our decision on the matter.',
      fr: "Nous avons procédé à l'étude de votre demande d'assurance et nous désirons vous aviser de notre décision à ce sujet.",
    },
  ];
  readonly postInputParagraphs = [
    {
      en: "For more information, don't hesitate to contact our customer service department at +1 (438) 806-7227. Our advisors will be happy to answer any questions you may have.",
      fr: "Pour de plus amples renseignements, n'hésitez pas à communiquer avec notre service à la clientèle au +1 (438) 806-7227, nos conseillers se feront un plaisir de répondre à vos questions.",
    },
    {
      en: 'Sincerely,',
      fr: 'Veuillez agréer nos salutations distinguées.',
    },
    {
      en: 'The team at Humania Assurance and Emma Services Financiers Inc.',
      fr: 'L’équipe de Humania Assurance et Emma Services Financiers Inc.',
    },
  ];

  constructor(readonly fb: FormBuilder) {
    this.form = fb.control<string | null>(null, [validators.notEmpty, hasNoInputPrompts]);

    this.dropdownForm = fb.control<string | null>(null);
    this.dropdownForm.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value) => this.form.patchValue(value));
  }

  onHide(): void {
    if (!this.form.value) {
      this.form.reset();
    }

    this.isDisplayedChange.emit(false);
  }

  submit(): void {
    this.isLoading = true;

    const translator = new EmTranslatePipe(`${this._info.languageCode}-CA`);

    const proposalRefusalMessage: TextTranslations = {
      en: `Date: ${this.refusalTimestamp}\n\n${this.headers.map((h) => h.en).join('\n')}\n\n`,
      fr: `Date : ${this.refusalTimestamp}\n\n${this.headers.map((h) => h.fr).join('\n')}\n\n`,
    };

    proposalRefusalMessage.en +=
      this.preInputParagraphs.map((p) => translator.transform(p, 'en')).join('\n\n') + '\n\n';
    proposalRefusalMessage.fr +=
      this.preInputParagraphs.map((p) => translator.transform(p, 'fr')).join('\n\n') + '\n\n';

    proposalRefusalMessage.en += this.form.value!.trim() + '\n\n';
    proposalRefusalMessage.fr += this.form.value!.trim() + '\n\n';

    proposalRefusalMessage.en += this.postInputParagraphs
      .map((p) => translator.transform(p, 'en'))
      .join('\n\n');
    proposalRefusalMessage.fr += this.postInputParagraphs
      .map((p) => translator.transform(p, 'fr'))
      .join('\n\n');

    this.submitRefusal.emit({
      refusalMessage: proposalRefusalMessage,
      callback: () => (this.isLoading = false),
    });
  }
}
